// Sprint 44 XL - Design tokens centrales (S46: Light Mode Premium).
// Fuente unica de verdad para colores, tipografia, espaciado, radios,
// sombras, motion y z-index. Consumido por:
//   - applyTheme(): setea variables CSS en documentElement.
//   - componentes XL via var(--xl-*) en sus stylesheets.
// Sin libs externas. Light/dark conviven via dark.js overrides.
//
// S46 changes:
//   - Paleta light premium (gris-azulada, NO blanco puro).
//   - Contraste AA en text-secondary y text-muted.
//   - Auto-deteccion prefers-color-scheme (init + listener live).
//   - setTheme wrapper con clase temporal xl-theme-changing (130ms).
//   - Emision de --xl-space-0 / 8 y --xl-z-*.
import { useEffect, useRef, useState } from 'react';
import darkOverrides from './dark';

const tokens = {
  color: {
    primary:      '#2563EB',
    primaryHover: '#1D4ED8',
    secondary:    '#059669',
    // S46 - paleta premium gris-azulada (revisada para contraste real).
    bg:           '#E4E9F2',
    surface:      '#FFFFFF',
    surfaceAlt:   '#D7DFEB',
    border:       '#D0D7E2',
    borderStrong: '#CBD5E1',
    text: {
      primary:   '#0E1525',
      // S46 - subidos para AA sobre surfaceAlt y bg.
      secondary: '#3E4A5C',
      muted:     '#525C6B',
      inverse:   '#FFFFFF',
    },
    state: {
      success: '#059669',
      warning: '#D97706',
      danger:  '#DC2626',
      info:    '#2563EB',
    },
    focus: '#2563EB',
  },
  font: {
    family: {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    },
    size:   { xs: '11px', sm: '12px', base: '14px', md: '15px', lg: '16px', xl: '20px' },
    weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  },
  spacing: { 0: '0', 1: '4px', 2: '8px', 3: '12px', 4: '16px', 5: '20px', 6: '24px', 8: '32px' },
  radius:  { sm: '6px', md: '8px', lg: '12px', xl: '16px', full: '9999px' },
  shadow:  {
    sm: '0 1px 2px rgba(0,0,0,0.04)',
    md: '0 2px 4px rgba(0,0,0,0.06)',
    lg: '0 4px 12px rgba(0,0,0,0.08)',
  },
  z: { dropdown: 1000, tooltip: 1100, modal: 1200, toast: 1300 },
};

function _pickDeep(obj, path) {
  return path.split('.').reduce((a, k) => (a == null ? a : a[k]), obj);
}

function _mergeColor(base, over) {
  // Merge shallow del sub-objeto color (text, state, etc.).
  if (!over) return base;
  const out = { ...base, ...over };
  if (over.text)  out.text  = { ...base.text,  ...over.text  };
  if (over.state) out.state = { ...base.state, ...over.state };
  return out;
}

function _themeMerge(theme) {
  if (theme !== 'dark') return tokens;
  return {
    ...tokens,
    color:  _mergeColor(tokens.color, darkOverrides.color),
    shadow: { ...tokens.shadow, ...(darkOverrides.shadow || {}) },
  };
}

/**
 * Aplica el tema (light/dark) escribiendo CSS vars en :root.
 * No re-render: los componentes XL leen var(--xl-*) y actualizan solos.
 */
export function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const t = _themeMerge(theme);
  const root = document.documentElement;
  const set = (k, v) => root.style.setProperty(k, v);

  set('--xl-color-primary',        t.color.primary);
  set('--xl-color-primary-hover',  t.color.primaryHover);
  set('--xl-color-secondary',      t.color.secondary);
  set('--xl-color-bg',             t.color.bg);
  set('--xl-color-surface',        t.color.surface);
  set('--xl-color-surface-alt',    t.color.surfaceAlt);
  set('--xl-color-border',         t.color.border);
  set('--xl-color-border-strong',  t.color.borderStrong);
  set('--xl-color-text',           t.color.text.primary);
  set('--xl-color-text-secondary', t.color.text.secondary);
  set('--xl-color-text-muted',     t.color.text.muted);
  set('--xl-color-text-inverse',   t.color.text.inverse);
  set('--xl-color-success',        t.color.state.success);
  set('--xl-color-warning',        t.color.state.warning);
  set('--xl-color-danger',         t.color.state.danger);
  set('--xl-color-info',           t.color.state.info);
  set('--xl-color-focus',          t.color.focus);
  set('--xl-shadow-sm',            t.shadow.sm);
  set('--xl-shadow-md',            t.shadow.md);
  set('--xl-shadow-lg',            t.shadow.lg);
  set('--xl-radius-sm',            t.radius.sm);
  set('--xl-radius-md',            t.radius.md);
  set('--xl-radius-lg',            t.radius.lg);
  set('--xl-radius-full',          t.radius.full);
  set('--xl-font-sans',            t.font.family.sans);
  set('--xl-font-mono',            t.font.family.mono);
  // S46 - emitir tokens faltantes para cerrar la deuda menor.
  set('--xl-space-0',              t.spacing[0]);
  set('--xl-space-1',              t.spacing[1]);
  set('--xl-space-2',              t.spacing[2]);
  set('--xl-space-3',              t.spacing[3]);
  set('--xl-space-4',              t.spacing[4]);
  set('--xl-space-5',              t.spacing[5]);
  set('--xl-space-6',              t.spacing[6]);
  set('--xl-space-8',              t.spacing[8]);
  set('--xl-z-dropdown',           String(t.z.dropdown));
  set('--xl-z-tooltip',            String(t.z.tooltip));
  set('--xl-z-modal',              String(t.z.modal));
  set('--xl-z-toast',              String(t.z.toast));
  root.dataset.xlTheme = theme;
}

// S46 - Helpers internos para preferencia del SO + clase temporal.

const STORAGE_KEY = 'xl-theme';
const TRANSITION_MS = 130; // 120ms fade + 10ms slack para evitar parpadeo.

function _readStoredTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

function _systemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function _initialTheme() {
  const stored = _readStoredTheme();
  if (stored) return stored;
  return _systemPrefersDark() ? 'dark' : 'light';
}

/**
 * Hook: aplica y persiste tema. Devuelve [theme, setTheme].
 * - Init: localStorage > prefers-color-scheme > 'light'.
 * - Listener live: si NO hay localStorage, sigue al SO mientras la app
 *   este abierta. En cuanto el usuario use el toggle, el listener se
 *   ignora porque pasa a haber localStorage.
 * - Toggle: anade clase temporal xl-theme-changing al <html> durante
 *   ~130ms para que motion.js haga fade global de 120ms en el arbol XL.
 */
export function useXLTheme() {
  const [theme, setThemeState] = useState(_initialTheme);
  const firstRun = useRef(true);

  useEffect(() => {
    const isFirst = firstRun.current;
    firstRun.current = false;

    // Toggle: clase temporal para activar la transicion global.
    let cleanupClass = null;
    if (!isFirst && typeof document !== 'undefined') {
      const root = document.documentElement;
      root.classList.add('xl-theme-changing');
      const tid = window.setTimeout(() => {
        root.classList.remove('xl-theme-changing');
      }, TRANSITION_MS);
      cleanupClass = () => {
        window.clearTimeout(tid);
        root.classList.remove('xl-theme-changing');
      };
    }

    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch {}

    return cleanupClass || undefined;
  }, [theme]);

  // Listener live de prefers-color-scheme: solo sigue al SO cuando NO
  // existe localStorage (es decir, el usuario aun no eligio manualmente).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (_readStoredTheme()) return; // usuario ya eligio: no pisar.
      setThemeState(e.matches ? 'dark' : 'light');
    };
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else if (mql.addListener) mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else if (mql.removeListener) mql.removeListener(handler);
    };
  }, []);

  return [theme, setThemeState];
}

export default tokens;
export { _pickDeep };
